import React, { useState } from 'react'
import { Button } from './Button'
import {
  Database,
  Clock,
  Code,
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  FolderSearch,
} from 'lucide-react'
import { SmartSearchResult } from '@/lib/smartSearch'

interface SmartSearchResultsProps {
  result: SmartSearchResult
  // open a result row where it lives in the app (table + id → route,
  // resolved via /api/smart-search/locate by the caller)
  onLocate?: (table: string, id: string | number) => void
  // preview a row WITHOUT navigating — the click default, so the results
  // behind it are never destroyed
  onPreview?: (row: Record<string, unknown>) => void
  // set by the server when the generated SQL returned no usable id: the
  // rows cannot be opened and must say so rather than look inert
  notOpenableReason?: string | null
  // hand the natural-language query to the primary system discovery surface
  onOpenLibrary?: () => void
}

// tables the locate endpoint can route to
const LOCATABLE = new Set(['projects', 'project_systems', 'systems_190', 'ra_system_assessments', 'contacts'])

// identifier columns the prompt asks the model to include for navigation —
// used for click-through, hidden from the visible table (kept in CSV export)
const HIDDEN_ID_COLUMNS = new Set([
  'id', 'project_id', 'project_system_id', 'ra_document_id',
  'system_profile_id', 'canonical_vendor_id', 'source_import_batch_id',
])

// generic cell rendering — columns are whatever the query selected, so we key
// off value type rather than hardcoded (now non-existent) column names.
function renderCell(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-slate-300 dark:text-slate-600">—</span>
  }
  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        value
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200'
          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
      }`}>
        {value ? 'Yes' : 'No'}
      </span>
    )
  }
  if (typeof value === 'number') {
    return <span className="tabular-nums">{value}</span>
  }
  const str = typeof value === 'object' ? JSON.stringify(value) : String(value)
  return <span className="block max-w-xs truncate" title={str}>{str}</span>
}

export const SmartSearchResults: React.FC<SmartSearchResultsProps> = ({
  result,
  onLocate,
  onPreview,
  notOpenableReason,
  onOpenLibrary,
}) => {
  const [showSQL, setShowSQL] = useState(false)
  const [showAllResults, setShowAllResults] = useState(false)
  const [filterColumn, setFilterColumn] = useState('')
  const [filterValue, setFilterValue] = useState('')

  const displayResults = showAllResults ? result.results : result.results.slice(0, 10)

  // apply client-side filtering
  const filteredResults = filterColumn && filterValue
    ? displayResults.filter(row =>
        String(row[filterColumn] || '').toLowerCase().includes(filterValue.toLowerCase())
      )
    : displayResults

  const exportToCSV = () => {
    if (result.results.length === 0) return

    const headers = Object.keys(result.results[0])
    const csvContent = [
      headers.join(','),
      ...result.results.map(row =>
        headers.map(header => {
          const value = row[header]
          // escape commas and quotes in csv
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`
          }
          return value || ''
        }).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `smart-search-results-${Date.now()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (!result.results.length) {
    return (
      <div className="rounded-lg bg-slate-50 p-6 text-center dark:bg-slate-800/50">
        <Database className="mx-auto mb-2 h-8 w-8 text-slate-300 dark:text-slate-600" />
        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">No results</p>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
          Try rephrasing your query or using different terms.
        </p>
        <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">Query: "{result.query}"</p>
      </div>
    )
  }

  // prefer the backend's ordered column list (SELECT order); fall back to the
  // first row's keys for older shapes
  const columns = result.columns?.length ? result.columns : Object.keys(result.results[0])
  // ids drive click-through, not reading — hide them unless they're all we have
  const visibleColumns = columns.filter(c => !HIDDEN_ID_COLUMNS.has(c))
  const displayColumns = visibleColumns.length ? visibleColumns : columns

  // where does a row live? primary table's id first (the prompt orders
  // tables_used primary-first), then FK fallbacks for join-shaped rows
  const primaryTable = result.tables_used?.find(t => LOCATABLE.has(t)) ?? null
  const rowTarget = (row: Record<string, unknown>): { table: string; id: string | number } | null => {
    if (primaryTable && row.id !== null && row.id !== undefined) {
      return { table: primaryTable, id: row.id as string | number }
    }
    if (row.project_system_id) return { table: 'project_systems', id: row.project_system_id as string }
    if (row.project_id) return { table: 'projects', id: row.project_id as string }
    return null
  }

  return (
    <div className="space-y-2">
      {/* the query returned no id we could use, so no row can be opened.
          Saying so beats rendering rows that look clickable and are not. */}
      {notOpenableReason && (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800
                      dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          {notOpenableReason}
        </p>
      )}
      {/* results header — dashboard panel */}
      <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-[10px] uppercase text-slate-400">Search results</p>
          <div className="flex items-center gap-3 text-[11px] tabular-nums text-slate-400">
            <span className="flex items-center gap-1">
              <Database className="h-3 w-3" />
              {result.results.length} results
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {result.executionTime}ms
            </span>
          </div>
        </div>
        {result.explanation && (
          <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">{result.explanation}</p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {onOpenLibrary && (
            <Button
              size="sm"
              onClick={onOpenLibrary}
              className="h-7 px-2 text-xs"
            >
              <FolderSearch className="mr-1.5 h-3.5 w-3.5" />
              Open in System Library
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSQL(!showSQL)}
            className="h-7 px-2 text-xs"
          >
            <Code className="mr-1.5 h-3.5 w-3.5" />
            {showSQL ? 'Hide' : 'Show'} SQL
            {showSQL ? <ChevronUp className="ml-1.5 h-3.5 w-3.5" /> : <ChevronDown className="ml-1.5 h-3.5 w-3.5" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            className="h-7 px-2 text-xs"
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export CSV
          </Button>
          <select
            value={filterColumn}
            onChange={(e) => setFilterColumn(e.target.value)}
            className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300"
          >
            <option value="">Filter by column…</option>
            {displayColumns.map(col => (
              <option key={col} value={col}>{col}</option>
            ))}
          </select>
          {filterColumn && (
            <input
              type="text"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder={`Filter ${filterColumn}…`}
              className="h-7 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-900/40 dark:text-white"
            />
          )}
          {filterColumn && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilterColumn('')
                setFilterValue('')
              }}
              className="h-7 px-2 text-xs"
            >
              Clear
            </Button>
          )}
        </div>

        {showSQL && (
          <pre className="mt-2 overflow-x-auto rounded-md bg-slate-100 p-2 font-mono text-[11px] text-slate-600 dark:bg-slate-900/60 dark:text-slate-300">
            {result.sql}
          </pre>
        )}
      </div>

      {/* results table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/50">
              <tr>
                {displayColumns.map(column => (
                  <th key={column} className="border-b border-slate-200 px-3 py-2 text-left font-medium text-slate-500 dark:border-slate-700 dark:text-slate-400">
                    {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </th>
                ))}
                {onLocate && (
                  <th className="border-b border-slate-200 px-3 py-2 text-left font-medium text-slate-500 dark:border-slate-700 dark:text-slate-400">
                    Open
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredResults.map((row, index) => {
                const target = onLocate ? rowTarget(row) : null
                return (
                  <tr
                    key={index}
                    onClick={
                      onPreview && target ? () => onPreview(row)
                      : target ? () => onLocate!(target.table, target.id)
                      : undefined
                    }
                    title={
                      onPreview && target ? 'Preview without leaving the results'
                      : target ? 'Open where this is located' : undefined
                    }
                    className={`border-b border-slate-200 last:border-b-0 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/40 ${
                      target ? 'cursor-pointer' : ''
                    }`}
                  >
                    {displayColumns.map(column => (
                      <td key={column} className="px-3 py-2 text-slate-900 dark:text-white">
                        {renderCell(row[column])}
                      </td>
                    ))}
                    {onLocate && (
                      <td className="px-3 py-2">
                        {target && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onLocate!(target.table, target.id) }}
                            className="h-7 w-7 p-0"
                            title="Open where this is located"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* show more */}
        {result.results.length > 10 && !showAllResults && (
          <div className="border-t border-slate-200 p-2.5 text-center dark:border-slate-700">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllResults(true)}
              className="h-7 px-2 text-xs"
            >
              <ChevronDown className="mr-1.5 h-3.5 w-3.5" />
              Show all {result.results.length} results
            </Button>
          </div>
        )}

        {/* summary */}
        <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400">
          Showing {filteredResults.length} of {result.results.length} results
          {filterColumn && filterValue && (
            <span> (filtered by {filterColumn}: "{filterValue}")</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default SmartSearchResults
